/**
 * Dashboard TanStack Query hooks.
 */

import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { dashboardKeys, chatKeys, userKeys } from "@/queries/keys";
import type { UserSubscription } from "@/features/subscription/types";
import type { FilterListItem } from "@/features/filter/api";

/** Drop the UI-only `displayValue` before a filter row crosses the wire. */
function stripFilterDisplayValue(list: FilterListItem[]): FilterListItem[] {
  return list.map(({ displayValue: _omit, ...rest }) => rest);
}

// -- Types -----------------------------------------------------------

export interface Activity {
  _id: string;
  type: string;
  title: string;
  createdAt: string;
  mediaId?: string;
  folderId?: string;
}

export interface OngoingActivity {
  _id: string;
  type: "mediaAnalysis" | "mediaTranscription";
  title: string;
  status: string;
  progress?: number;
  createdAt?: string;
}

export interface UpcomingMeeting {
  _id: string;
  title: string;
  startTime: string;
  endTime: string;
  platform: string;
  currentStatus?: string;
  source?: string;
  meetingURL?: string;
}

export interface ChatStatistics {
  totalCharacters: number;
  totalPrompts: number;
}

// The /prompt/statistics endpoint returns `totalRequest`; expose it as `totalPrompts`.
interface PromptStatsResponse {
  totalRequest: number;
  totalCharacters: number;
}

function toChatStatistics(d: PromptStatsResponse): ChatStatistics {
  return { totalPrompts: d.totalRequest, totalCharacters: d.totalCharacters };
}

export interface TextNoteStatistics {
  totalNotes: number;
  availableNotes: number;
  analyzedNotes: number;
  wordCount: number;
  characterCount: number;
  fileSize: number;
}

export interface MediaStatisticsResponse {
  totalMedia: number;
  analyzedMedia: number;
  notAnalyzedMedia: number;
  analyzedMinutes: number; // Note: field name is misleading — API returns SECONDS
  fileSize: number;
}

export interface MediaStorageStatistics {
  fileSize: number;
  totalMedia: number;
}

// -- Query Hooks -----------------------------------------------------

export function useRecentActivities() {
  return useQuery({
    queryKey: dashboardKeys.activities(),
    queryFn: async () => {
      const res = await apiClient.get<{ complete: Activity[] }>(
        "/analytics/activity/top"
      );
      if (!res.success) throw new Error(res.error ?? "Failed to fetch activities");
      return res.data?.complete ?? [];
    },
  });
}

export function useOngoingActivities() {
  return useQuery({
    queryKey: dashboardKeys.ongoingActivities(),
    queryFn: async () => {
      const res = await apiClient.get<{ ongoing: OngoingActivity[] }>(
        "/analytics/activity/ongoing"
      );
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch ongoing activities");
      return res.data?.ongoing ?? [];
    },
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useUpcomingMeetings(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: dashboardKeys.upcomingMeetings(),
    queryFn: async () => {
      const res = await apiClient.get<{ meetings: UpcomingMeeting[] }>(
        "/analytics/activity/meetings"
      );
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch upcoming meetings");
      return res.data?.meetings ?? [];
    },
    enabled: options.enabled ?? true,
  });
}

export function useAccountUsage(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: dashboardKeys.usage(),
    queryFn: async () => {
      const res = await apiClient.get<UserSubscription>(
        "/subscription",
        undefined,
        { trackingCategory: "subscription" },
      );
      if (!res.success) {
        throw new Error(res.error ?? "Failed to fetch subscription");
      }
      return res.data!;
    },
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
}

export function useChatUsage(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: dashboardKeys.chatUsage(startDate, endDate),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const queryString = params.toString();
      const res = await apiClient.get<PromptStatsResponse>(
        `/prompt/statistics${queryString ? `?${queryString}` : ""}`
      );
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch chat statistics");
      return toChatStatistics(res.data!);
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!startDate && !!endDate,
  });
}

export function useTotalChatUsage(enabled = true) {
  return useQuery({
    queryKey: chatKeys.total(),
    queryFn: async () => {
      const res = await apiClient.get<PromptStatsResponse>(
        "/prompt/statistics",
      );
      if (!res.success) throw new Error(res.error || "Failed to fetch chat stats");
      return toChatStatistics(res.data!);
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled,
  });
}

export function useTextNoteStats(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: dashboardKeys.textNotes(startDate, endDate),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const queryString = params.toString();
      const res = await apiClient.get<TextNoteStatistics>(
        `/text/statistics${queryString ? `?${queryString}` : ""}`
      );
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch text note stats");
      return res.data!;
    },
  });
}

export function useStorageStats(
  startDate?: string,
  endDate?: string,
  options?: { folderId?: string; filterList?: FilterListItem[] },
) {
  const folderId = options?.folderId;
  const filterList = options?.filterList;
  return useQuery({
    queryKey: dashboardKeys.storageStats(startDate, endDate, folderId, filterList),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      if (folderId) params.set("folderId", folderId);
      if (filterList && filterList.length > 0) {
        params.set("filterList", JSON.stringify(stripFilterDisplayValue(filterList)));
      }
      const queryString = params.toString();
      const res = await apiClient.get<MediaStorageStatistics>(
        `/media/statistics${queryString ? `?${queryString}` : ""}`
      );
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch storage stats");
      return res.data!;
    },
    enabled: !!startDate && !!endDate,
  });
}

export function useMediaTypeStats(
  mediaType: "audio" | "video",
  startDate?: string,
  endDate?: string,
) {
  return useQuery({
    queryKey: userKeys.mediaStats(mediaType, startDate, endDate),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const q = params.toString();
      const res = await apiClient.get<MediaStatisticsResponse>(
        `/media/statistics?mediaType=${mediaType}${q ? `&${q}` : ""}`,
      );
      if (!res.success) throw new Error(res.error ?? `Failed to fetch ${mediaType} stats`);
      return res.data!;
    },
  });
}

export function useProfilePromptStats(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: userKeys.promptStats(startDate, endDate),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const q = params.toString();
      const res = await apiClient.get<PromptStatsResponse>(
        `/prompt/statistics${q ? `?${q}` : ""}`,
      );
      if (!res.success) throw new Error(res.error ?? "Failed to fetch prompt stats");
      return toChatStatistics(res.data!);
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
